import { useState, useEffect, useRef, useCallback } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/Dialog'
import { Button } from '../ui/Button'
import { ReassuringLoader } from '../ui/ReassuringLoader'
import { RefreshCw, Trash2, Play, Save, Clock, Plus, GripVertical, ChevronDown, ChevronRight, Lock, CheckCircle2 } from 'lucide-react'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { BreakdownStep, JourneyChildType, JourneyDraft } from '../../types/journey'

// ── Draft persistence helpers ──

function saveDraftToStorage(key: string, entityId: number, entityTitle: string, steps: BreakdownStep[]): void {
  try {
    const draft: JourneyDraft = { entityId, entityTitle, steps, savedAt: new Date().toISOString() }
    localStorage.setItem(`${key}_${entityId}`, JSON.stringify(draft))
  } catch { /* quota exceeded, ignore */ }
}

function loadDraftFromStorage(key: string, entityId: number): JourneyDraft | null {
  try {
    const raw = localStorage.getItem(`${key}_${entityId}`)
    if (!raw) return null
    return JSON.parse(raw) as JourneyDraft
  } catch { return null }
}

function clearDraftFromStorage(key: string, entityId: number): void {
  localStorage.removeItem(`${key}_${entityId}`)
}

function formatDraftAge(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'yesterday'
  return `${days}d ago`
}

export function fmtDuration(mins?: number): string | null {
  if (!mins) return null
  if (mins >= 60) {
    const h = Math.floor(mins / 60)
    const m = mins % 60
    return m > 0 ? `${h}h ${m}m` : `${h}h`
  }
  return `${mins}m`
}

// ── SortableStep ──

const CHILD_TYPE_LABELS: Record<JourneyChildType, string> = {
  task: 'Task',
  skill: 'Skill',
  habit: 'Habit',
  roadblock: 'Roadblock',
  goal: 'Goal',
}

interface SortableStepProps {
  id: string
  step: BreakdownStep
  stepNumber: number
  isFirst: boolean
  isExpanded: boolean
  isLocked: boolean
  showTypeSelector?: boolean
  allowedTypes?: JourneyChildType[]
  onToggleExpand: () => void
  onUpdate: (field: keyof BreakdownStep, value: any) => void
  onRemove: () => void
  /** Fields that depend on the kind (a habit step's schedule), rendered in the expanded area. */
  renderExtra?: (step: BreakdownStep, update: (field: keyof BreakdownStep, value: unknown) => void) => React.ReactNode
  /** The steps this one comes after, named. Read-only: the shape is the planner's. */
  afterLabel?: string | null
}

function SortableStep({
  id, step, stepNumber, isFirst, isExpanded, isLocked,
  showTypeSelector, allowedTypes,
  onToggleExpand, onUpdate, onRemove, renderExtra, afterLabel,
}: SortableStepProps) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id, disabled: isLocked })

  const style = { transform: CSS.Transform.toString(transform), transition }

  if (isLocked) {
    return (
      <div className="border border-soft rounded-lg bg-surface-alt/50 opacity-60">
        <div className="flex items-center gap-2 p-3">
          <Lock className="h-4 w-4 text-muted flex-shrink-0" />
          <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold bg-ok/20 text-ok">
            <CheckCircle2 className="h-4 w-4" />
          </div>
          <span className="flex-1 text-sm font-medium text-secondary line-through truncate">{step.title}</span>
          {step.estimated_duration_minutes > 0 && (
            <span className="flex items-center gap-1 text-xs text-muted flex-shrink-0">
              <Clock className="h-3 w-3" />
              {fmtDuration(step.estimated_duration_minutes)}
            </span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div ref={setNodeRef} style={style}
      className={`border border-soft rounded-lg overflow-hidden ${isDragging ? 'opacity-50 shadow-lg ring-2 ring-brand/30' : ''}`}
      data-testid="journey-step" data-kind={step.child_type || 'task'}
    >
      <div className="flex flex-wrap items-center gap-2 p-2 sm:p-3">
        <div {...listeners} {...attributes}
          className="flex-shrink-0 cursor-grab active:cursor-grabbing touch-none p-0.5 text-muted hover:text-primary"
        >
          <GripVertical className="h-4 w-4" />
        </div>

        <div className={`flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-xs font-bold ${
          isFirst ? 'bg-brand text-on-brand' : 'bg-surface-alt text-muted'
        }`}>
          {stepNumber}
        </div>

        <input
          type="text"
          value={step.title}
          onChange={e => onUpdate('title', e.target.value)}
          placeholder="Step title..."
          className="flex-1 min-w-0 text-sm font-medium text-primary bg-transparent border-b border-transparent focus:border-brand outline-none px-1 py-0.5"
        />

        {showTypeSelector && allowedTypes && allowedTypes.length > 1 && (
          <select
            value={step.child_type || allowedTypes[0]}
            onChange={e => onUpdate('child_type', e.target.value)}
            aria-label="What this step creates"
            className="flex-shrink-0 text-xs bg-surface-alt border border-soft rounded px-2 py-1 focus:border-brand outline-none"
            data-testid="journey-step-kind"
          >
            {allowedTypes.map(t => (
              <option key={t} value={t}>{CHILD_TYPE_LABELS[t]}</option>
            ))}
          </select>
        )}

        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button type="button" className="p-1 text-muted hover:text-primary" onClick={onToggleExpand}
            aria-expanded={isExpanded} aria-label={`${isExpanded ? 'Hide' : 'Show'} the details of ${step.title || 'this step'}`}
            data-testid="journey-step-expand"
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          <button type="button" onClick={onRemove} className="p-1 text-muted hover:text-bad" title="Remove step">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="flex items-center gap-1 w-full sm:w-auto order-last sm:order-none pl-[2.75rem] sm:pl-0 mt-1 sm:mt-0">
          <Clock className="h-3 w-3 text-muted flex-shrink-0" />
          <input
            type="number" min={1}
            value={step.estimated_duration_minutes || ''}
            onChange={e => onUpdate('estimated_duration_minutes', parseInt(e.target.value) || 0)}
            className="w-14 text-xs text-secondary bg-surface-alt rounded px-1.5 py-0.5 border border-soft focus:border-brand outline-none text-center"
          />
          <span className="text-xs text-muted">min</span>
        </div>
      </div>

      {(afterLabel || step.demonstrates_level) && (
        <p className="px-3 pb-2 pl-[2.75rem] sm:pl-[4.5rem] text-xs text-muted" data-testid="journey-step-after">
          {afterLabel ? `Comes after ${afterLabel}` : ''}
          {afterLabel && step.demonstrates_level ? ' \u00b7 ' : ''}
          {step.demonstrates_level ? `shows you are at ${step.demonstrates_level}` : ''}
        </p>
      )}

      {isExpanded && (
        <div className="px-3 pb-3 pl-[2.75rem] sm:pl-[4.5rem] space-y-2">
          <textarea
            value={step.description || ''}
            onChange={e => onUpdate('description', e.target.value)}
            placeholder="Describe what this step covers..."
            rows={2}
            className="w-full text-sm text-secondary bg-surface-alt rounded-md border border-soft p-2 focus:border-brand outline-none resize-none"
          />
          {renderExtra?.(step, onUpdate)}
        </div>
      )}
    </div>
  )
}

// ── Main Modal Component ──

export interface JourneyPreviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  steps: BreakdownStep[] | null
  /** While true and steps are not yet in, show a reassuring "mapping your
   *  journey…" loader instead of an empty "No steps generated" state. */
  loading?: boolean
  onStepsChange: (steps: BreakdownStep[]) => void
  onAccept: (steps: BreakdownStep[]) => Promise<void>
  onSaveEdit?: (steps: BreakdownStep[]) => Promise<void>
  isEditingActive?: boolean
  completedCount?: number
  accepting?: boolean
  savingEdit?: boolean
  draftKey: string
  entityId: number
  entityTitle: string
  showStepTypeSelector?: boolean
  stepTypes?: JourneyChildType[]
  headerExtra?: React.ReactNode
  resumedFromDraft?: boolean
  onDiscardDraft?: () => void
  /** Per-kind fields inside a step (a habit step's schedule). */
  renderStepExtra?: (step: BreakdownStep, update: (field: keyof BreakdownStep, value: unknown) => void) => React.ReactNode
}

export function JourneyPreviewModal({
  open, onOpenChange, title, description,
  steps, loading = false, onStepsChange,
  onAccept, onSaveEdit,
  isEditingActive = false,
  completedCount = 0,
  accepting = false,
  savingEdit = false,
  draftKey, entityId, entityTitle,
  showStepTypeSelector = false,
  stepTypes,
  headerExtra,
  resumedFromDraft = false,
  onDiscardDraft,
  renderStepExtra,
}: JourneyPreviewModalProps) {

  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set())
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Auto-save debounced draft
  useEffect(() => {
    if (!open || !steps || Number.isNaN(entityId)) return
    if (isEditingActive) return

    if (draftTimerRef.current) clearTimeout(draftTimerRef.current)
    draftTimerRef.current = setTimeout(() => {
      saveDraftToStorage(draftKey, entityId, entityTitle, steps)
    }, 500)

    return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current) }
  }, [steps, open, entityId, entityTitle, draftKey, isEditingActive])

  const handleClose = useCallback(() => {
    if (isEditingActive) {
      onOpenChange(false)
      return
    }
    // Flush draft
    if (steps && !Number.isNaN(entityId)) {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current)
      saveDraftToStorage(draftKey, entityId, entityTitle, steps)
    }
    onOpenChange(false)
  }, [isEditingActive, steps, entityId, entityTitle, draftKey, onOpenChange])

  const toggleExpanded = (idx: number) => {
    setExpandedSteps(prev => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx); else next.add(idx)
      return next
    })
  }

  const updateStep = (idx: number, field: keyof BreakdownStep, value: any) => {
    if (!steps) return
    const updated = [...steps]
    updated[idx] = { ...updated[idx], [field]: value }
    onStepsChange(updated)
  }

  const removeStep = (idx: number) => {
    if (!steps) return
    const updated = steps.filter((_, i) => i !== idx)
    updated.forEach((s, i) => { s.order = i + 1 })
    onStepsChange(updated)
    setExpandedSteps(prev => {
      const next = new Set<number>()
      prev.forEach(i => { if (i < idx) next.add(i); else if (i > idx) next.add(i - 1) })
      return next
    })
  }

  const addStep = () => {
    if (!steps) return
    const newStep: BreakdownStep = {
      title: '',
      description: '',
      estimated_duration_minutes: 30,
      order: steps.length + 1,
      child_type: stepTypes?.[0],
    }
    onStepsChange([...steps, newStep])
    setExpandedSteps(prev => new Set([...prev, steps.length]))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id || !steps) return

    const oldIndex = parseInt(String(active.id).replace('step-', ''), 10)
    const newIndex = parseInt(String(over.id).replace('step-', ''), 10)
    if (Number.isNaN(oldIndex) || Number.isNaN(newIndex)) return

    const updated = arrayMove(steps, oldIndex, newIndex)
    updated.forEach((s, i) => { s.order = i + 1 })
    onStepsChange(updated)

    setExpandedSteps(prev => {
      const next = new Set<number>()
      prev.forEach(i => {
        if (i === oldIndex) next.add(newIndex)
        else if (oldIndex < newIndex && i > oldIndex && i <= newIndex) next.add(i - 1)
        else if (oldIndex > newIndex && i >= newIndex && i < oldIndex) next.add(i + 1)
        else next.add(i)
      })
      return next
    })
  }

  const totalDuration = steps?.reduce((sum, s) => sum + (s.estimated_duration_minutes || 0), 0) || 0
  const hasValidSteps = steps?.length && steps.some(s => s.title.trim())

  // Naming what a step comes after, so the person can see the shape they are
  // accepting rather than reading the list as a queue. A prerequisite whose step
  // was removed is simply not named — the server prunes it on save.
  //
  // A goal step can also wait on a capability, which is a different kind of
  // waiting and is named as one: unnamed, half the shape of the journey would be
  // invisible in the very screen where it is agreed to.
  const titleByRef = new Map(
    (steps || []).filter(s => s.step_ref).map(s => [s.step_ref as string, s.title])
  )
  const afterLabelFor = (step: BreakdownStep) => {
    const parts = (step.prerequisite_step_refs || [])
      .map(ref => titleByRef.get(ref))
      .filter((t): t is string => Boolean(t && t.trim()))
    for (const req of step.skill_requirements || []) {
      const skill = titleByRef.get(req.skill_step_ref)
      if (!skill?.trim()) continue
      parts.push(req.required_level ? `${skill} at ${req.required_level}` : skill)
    }
    return parts.length ? parts.join(' and ') : null
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {headerExtra && <div className="px-6 pb-2">{headerExtra}</div>}

        {/* Duration summary + drag hint */}
        {steps && steps.length > 0 && (
          <div className="px-6 pb-1 flex items-center justify-between">
            <p className="text-xs text-muted">
              Drag to reorder{isEditingActive && completedCount > 0 ? ' \u2014 completed steps are locked' : ''}
            </p>
            {totalDuration > 0 && (
              <span className="flex items-center gap-1 text-xs text-secondary">
                <Clock className="h-3.5 w-3.5" /> {fmtDuration(totalDuration)} total
              </span>
            )}
          </div>
        )}

        {/* Steps list */}
        <div className="px-6 pb-4 max-h-[60vh] overflow-y-auto flex-1 min-h-0">
          {steps && steps.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <div className="space-y-2">
                {isEditingActive && completedCount > 0 && steps.slice(0, completedCount).map((step, idx) => (
                  <SortableStep
                    key={`locked-${idx}`} id={`step-${idx}`} step={step} stepNumber={idx + 1}
                    isFirst={false} isExpanded={false} isLocked={true}
                    showTypeSelector={showStepTypeSelector} allowedTypes={stepTypes}
                    onToggleExpand={() => {}} onUpdate={() => {}} onRemove={() => {}}
                    afterLabel={afterLabelFor(step)}
                  />
                ))}

                <SortableContext
                  items={steps.slice(completedCount).map((_, i) => `step-${completedCount + i}`)}
                  strategy={verticalListSortingStrategy}
                >
                  {steps.slice(completedCount).map((step, relIdx) => {
                    const idx = completedCount + relIdx
                    return (
                      <SortableStep
                        key={`step-${idx}`} id={`step-${idx}`} step={step} stepNumber={idx + 1}
                        isFirst={idx === 0 || (isEditingActive && idx === completedCount)}
                        isExpanded={expandedSteps.has(idx)} isLocked={false}
                        showTypeSelector={showStepTypeSelector} allowedTypes={stepTypes}
                        onToggleExpand={() => toggleExpanded(idx)}
                        onUpdate={(field, value) => updateStep(idx, field, value)}
                        onRemove={() => removeStep(idx)}
                        renderExtra={renderStepExtra}
                        afterLabel={afterLabelFor(step)}
                      />
                    )
                  })}
                </SortableContext>

                <button type="button" onClick={addStep}
                  className="w-full flex items-center justify-center gap-2 p-2.5 border border-dashed border-soft rounded-lg text-sm text-muted hover:text-primary hover:border-strong transition-colors"
                >
                  <Plus className="h-4 w-4" /> Add Step
                </button>
              </div>
            </DndContext>
          ) : loading ? (
            <ReassuringLoader
              title="Mapping your journey…"
              messages={[
                'Breaking this goal into doable steps…',
                'Ordering them so each builds on the last…',
                'Tailoring the plan to what matters to you…',
                'Almost ready — polishing your roadmap…',
              ]}
            />
          ) : (
            <div className="text-center py-8 text-muted text-sm space-y-2">
              <p>No steps generated.</p>
              <button type="button" onClick={addStep} className="text-brand hover:underline text-sm">
                Add a step manually
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex flex-wrap items-center gap-3 border-t border-soft pt-4">
          {isEditingActive && onSaveEdit ? (
            <Button onClick={() => onSaveEdit(steps || [])} disabled={savingEdit || !hasValidSteps}>
              {savingEdit ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {savingEdit ? 'Saving...' : 'Save Changes'}
            </Button>
          ) : (
            <Button onClick={() => onAccept(steps || [])} disabled={accepting || !hasValidSteps}>
              {accepting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              {accepting ? 'Starting journey...' : 'Start Journey'}
            </Button>
          )}
          <Button variant="outline" onClick={handleClose} disabled={accepting || savingEdit}>
            Cancel
          </Button>
          {resumedFromDraft && !isEditingActive && onDiscardDraft && (
            <Button variant="outline" className="text-bad hover:bg-bad/10" onClick={onDiscardDraft} disabled={accepting}>
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Discard Draft
            </Button>
          )}
          <span className="ml-auto text-xs text-muted">
            {steps?.length || 0} step{(steps?.length || 0) !== 1 ? 's' : ''}
            {totalDuration > 0 && ` \u00b7 ${fmtDuration(totalDuration)}`}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// Re-export draft helpers for use by parent pages
export { saveDraftToStorage, loadDraftFromStorage, clearDraftFromStorage, formatDraftAge }
